import {createEntityMeta,SyncStatus} from '../offline/entity.js';
import {
  archiveThermometer,
  createThermometer,
  updateThermometer
} from './thermometer.js';

export const THERMOMETER_ENTITY_TYPE='thermometer';

export class ThermometerConflictError extends Error{
  constructor(id){
    super('THERMOMETER_CONFLICT');
    this.name='ThermometerConflictError';
    this.thermometerId=id;
  }
}

function requireDependencies(repository,queue){
  if(!repository?.get||!repository?.list||!repository?.put)throw new TypeError('repository with get/list/put is required');
  if(!queue?.enqueue||!queue?.markConflict)throw new TypeError('sync queue with enqueue/markConflict is required');
}

function withMeta(existing,domain){
  const value={...existing};
  delete value.key;
  delete value.entityType;
  return {...value,...domain,id:domain.id};
}

export function createThermometerStore({repository,queue,now=()=>new Date().toISOString()}={}){
  requireDependencies(repository,queue);

  async function get(id){
    return repository.get(THERMOMETER_ENTITY_TYPE,String(id||'').trim());
  }

  async function list({userId=null,includeArchived=false}={}){
    const rows=await repository.list(THERMOMETER_ENTITY_TYPE);
    return Object.freeze((rows||[])
      .filter(row=>!userId||row.userId===userId)
      .filter(row=>includeArchived||!row.archivedAt)
      .sort((a,b)=>
        String(a.name||'').localeCompare(String(b.name||''))
        ||String(a.id||'').localeCompare(String(b.id||''))
      ));
  }

  async function create(input){
    const domain=createThermometer(input);
    const entity={
      ...createEntityMeta({id:domain.id,now:now(),syncStatus:SyncStatus.PENDING_CREATE}),
      ...domain
    };
    return queue.enqueue(THERMOMETER_ENTITY_TYPE,entity,'create');
  }

  async function edit(id,changes){
    const current=await get(id);
    if(!current)throw new TypeError('Thermometer not found');
    if(current.syncStatus===SyncStatus.CONFLICT)throw new ThermometerConflictError(current.id);
    const domain=updateThermometer(current,changes);
    return queue.enqueue(THERMOMETER_ENTITY_TYPE,withMeta(current,domain),'update');
  }

  async function archive(id){
    const current=await get(id);
    if(!current)throw new TypeError('Thermometer not found');
    if(current.syncStatus===SyncStatus.CONFLICT)throw new ThermometerConflictError(current.id);
    const domain=archiveThermometer(current,{now:now()});
    return queue.enqueue(THERMOMETER_ENTITY_TYPE,withMeta(current,domain),'update');
  }

  async function markConflict(id,conflict){
    const current=await get(id);
    if(!current)throw new TypeError('Thermometer not found');
    return queue.markConflict(THERMOMETER_ENTITY_TYPE,current,conflict);
  }

  return Object.freeze({get,list,create,edit,archive,markConflict});
}
